import { useState, useEffect } from "react";
import Button from "../../../components/Button";
import { RoomRateMappingPayload } from "../types";
import { withIds } from "../utils";

type MappingWithId = RoomRateMappingPayload & { _id: string };

const EMPTY_MAPPING: RoomRateMappingPayload = {
  pmsRoomTypeId: "",
  googleRoomTypeId: "",
  pmsRateId: "",
  googleRateId: "",
  baseOccupancy: 2,
  maxOccupancy: 4,
};

export function RoomRateMappingEditor({
  initialMappings,
  onSave,
  isSaving,
}: {
  initialMappings: RoomRateMappingPayload[];
  onSave: (mappings: RoomRateMappingPayload[]) => void;
  isSaving: boolean;
}) {
  const [mappings, setMappings] = useState<MappingWithId[]>(() => withIds(initialMappings));

  useEffect(() => {
    setMappings(withIds(initialMappings));
  }, [initialMappings]);

  const updateMapping = (
    index: number,
    field: keyof RoomRateMappingPayload,
    value: string | number,
  ) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const removeMapping = (index: number) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Room-Rate Mappings</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setMappings((prev) => [...prev, { ...EMPTY_MAPPING, _id: crypto.randomUUID() } as MappingWithId])
            }
          >
            Add Mapping
          </Button>
          <Button
            size="sm"
            onClick={() => onSave(mappings.map(({ _id, ...rest }) => rest))}
            loading={isSaving}
            disabled={isSaving}
          >
            Save All
          </Button>
        </div>
      </div>

      {mappings.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">
          No room-rate mappings configured.
        </p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F4F6]">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase rounded-tl-lg rounded-bl-lg">
                  PMS Room Type ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Google Room Type ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  PMS Rate ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Google Rate ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Base Occ.
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Max Occ.
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase rounded-tr-lg rounded-br-lg">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping, idx) => (
                <tr key={mapping._id} className="border-b border-b-[#E9E9EB]">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={mapping.pmsRoomTypeId}
                      onChange={(e) =>
                        updateMapping(idx, "pmsRoomTypeId", e.target.value)
                      }
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={mapping.googleRoomTypeId}
                      onChange={(e) =>
                        updateMapping(idx, "googleRoomTypeId", e.target.value)
                      }
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={mapping.pmsRateId}
                      onChange={(e) =>
                        updateMapping(idx, "pmsRateId", e.target.value)
                      }
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={mapping.googleRateId}
                      onChange={(e) =>
                        updateMapping(idx, "googleRateId", e.target.value)
                      }
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      value={mapping.baseOccupancy}
                      onChange={(e) =>
                        updateMapping(
                          idx,
                          "baseOccupancy",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-16 border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      value={mapping.maxOccupancy}
                      onChange={(e) =>
                        updateMapping(
                          idx,
                          "maxOccupancy",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-16 border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => removeMapping(idx)}
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
